'use strict';

import React, {Component} from 'react';
import {connect} from 'react-redux';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {
    InputSwitch,
    InputSwitchChangeEvent
} from 'primereact/inputswitch';
import {Panel} from 'primereact/panel';

import ContentButton, {
    ReferenceType as ContentButtonType
} from '~/frontend/src/components/base/shared/ContentButton';

import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import User from '~/shared/src/model/User';
import PropertySet from '~/shared/src/mm/PropertySet';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';

import {StoreState} from '~/frontend/src/store';
import {
    getPropertySetDisplayName,
    getReferenceSetDisplayName,
    getReferenceSetDisplayNameFromItemType
} from '~/frontend/src/utilities/renderHelpers';


const defaultProps = {

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    id : string;

    /**
     * The css class for the button
     */
    buttonClassName : string;

    /**
     * The css class for the buttons icon
     */
    buttonIconClassName : string;

    buttonTooltip : string;

    /**
     * The title of the menu
     */
    menuTitle : string;

    /**
     * The description of the menu
     */
    menuDescription? : string;

    /**
     * The properties
     */
    allPropertySets? : PropertySet[];

    /**
     * The selectable properties
     *
     * Only applicable if allPropertySets is set!
     */
    selectablePropertySets? : PropertySet[];

    /**
     * The selected properties
     *
     * Only applicable if allPropertySets is set!
     */
    selectedPropertySets? : PropertySet[];

    /**
     * The enumerated properties
     */
    allEnumPropertySets? : PropertySet[];

    /**
     * The selectable enumerated properties
     *
     * Only applicable if allPropertySets is set!
     */
    selectableEnumPropertySets? : PropertySet[];

    /**
     * The selected enumerated properties
     *
     * Only applicable if allPropertySets is set!
     */
    selectedEnumPropertySets? : PropertySet[];

    /**
     * The references
     */
    allReferenceSets? : { dest : ReferenceSet[]; src : ReferenceSet[]; };

    /**
     * The selectable references
     *
     * Only applicable if allPropertySets is set!
     */
    selectableReferenceSets? : { dest : ReferenceSet[]; src : ReferenceSet[]; };

    /**
     * The selected references
     *
     * Only applicable if allPropertySets is set!
     */
    selectedReferenceSets? : { dest : ReferenceSet[]; src : ReferenceSet[]; };

    /**
     * The ItemType to label the references for
     */
    referenceItemType? : string;

    /**
     * Callback to invoke on apply.
     */
    onApply? : ( selectedPropertySets : PropertySet[], selectedEnumPropertySets : PropertySet[], selectedReferenceSets : { dest : ReferenceSet[]; src : ReferenceSet[]; } ) => void;

    /**
     * The CSS class name(s) to add to the component.
     */
    className? : string;

    /**
     * Callback if the button will expand.
     * Return false to avoid expanding.
     */
    onExpand? : ( event : React.MouseEvent<HTMLButtonElement> ) => boolean;

    /**
     * Callback if the button will collapse.
     * Return false to avoid collapsing.
     */
    onCollapse? : ( event : React.MouseEvent, isOutsideClicked : boolean, eventPhase : 'beforeClick' | 'afterClick' | 'collapse' ) => boolean;

    tabIndex? : number;
};

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps;

type State = {
    currentSelectedPropertySets : PropertySet[];
    selectedPropertySets : PropertySet[];
    currentSelectedEnumPropertySets : PropertySet[];
    selectedEnumPropertySets : PropertySet[];
    currentSelectedReferenceSets : { dest : ReferenceSet[]; src : ReferenceSet[]; };
    selectedReferenceSets : { dest : ReferenceSet[]; src : ReferenceSet[]; };
};


type SelectionType = 'properties' | 'enumProperties' | 'destReferences' | 'srcReferences';


/**
 * Menu for selection of properties (incl. enumerated properties) and references
 */
class PropertiesAndReferencesSelectionMenu extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    contentButton? : ContentButtonType | null;

    constructor( props : AllProps ) {
        super( props );

        this.state = {
            currentSelectedPropertySets    : [],
            selectedPropertySets           : [],
            currentSelectedEnumPropertySets: [],
            selectedEnumPropertySets       : [],
            currentSelectedReferenceSets   : {
                dest: [],
                src : []
            },
            selectedReferenceSets          : {
                dest: [],
                src : []
            }
        };

        // bind event handlers
        this.onApplyButtonClick = this.onApplyButtonClick.bind( this );
        this.onCancelButtonClick = this.onCancelButtonClick.bind( this );
        this.onContentButtonExpand = this.onContentButtonExpand.bind( this );
        this.onContentButtonCollapse = this.onContentButtonCollapse.bind( this );
    }

    /**
     * REACT lifecycle method
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) {
        const retVal : Partial<State>                                   = {},
              changedProps : ( Extract<keyof AllProps, keyof State> )[] = keys<State>().reduce(
                  ( innerRetVal : ( Extract<keyof AllProps, keyof State> )[], prop ) => {
                      if (
                          props[ prop as keyof AllProps ] !== undefined
                          && props[ prop as keyof AllProps ] !== state[ prop ]
                      ) {
                          innerRetVal.push( prop as Extract<keyof AllProps, keyof State> );
                      }

                      return innerRetVal;
                  },
                  []
              );

        // reset selectedPropertySets
        if ( changedProps.includes( 'selectedPropertySets' ) ) {
            retVal.currentSelectedPropertySets = ( [] as PropertySet[] ).concat( props.selectedPropertySets ?? [] );
        }

        // reset selectedEnumPropertySets
        if ( changedProps.includes( 'selectedEnumPropertySets' ) ) {
            retVal.currentSelectedEnumPropertySets = ( [] as PropertySet[] ).concat( props.selectedEnumPropertySets ?? [] );
        }

        // reset selectedReferenceSets
        if ( changedProps.includes( 'selectedReferenceSets' ) ) {
            retVal.currentSelectedReferenceSets = {
                dest: ( [] as ReferenceSet[] ).concat( props.selectedReferenceSets?.dest ?? [] ),
                src : ( [] as ReferenceSet[] ).concat( props.selectedReferenceSets?.src ?? [] )
            };
        }

        // update state
        for ( const prop of changedProps ) {
            // @ts-expect-error changedProps is only one of the props also contained in state (which are type equal)
            retVal[ prop ] = props[ prop ] as State[ typeof prop ];
        }

        return retVal;
    }

    onApplyButtonClick( event : React.MouseEvent ) {
        if ( this.props.onApply ) {
            this.props.onApply( this.state.currentSelectedPropertySets, this.state.currentSelectedEnumPropertySets, this.state.currentSelectedReferenceSets );
        }

        this.contentButton?.collapse( event );
    }

    onCancelButtonClick( event : React.MouseEvent ) {
        this.contentButton?.collapse( event );
    }

    onContentButtonExpand( event : React.MouseEvent<HTMLButtonElement, MouseEvent> ) {
        let retVal : boolean = true;

        if ( this.props.onExpand ) {
            retVal = this.props.onExpand( event );
        }

        return retVal;
    }

    onContentButtonCollapse( event : React.MouseEvent, isOutsideClicked : boolean, eventPhase : 'beforeClick' | 'afterClick' | 'collapse' ) {
        let retVal : boolean = isOutsideClicked;

        if ( this.props.onCollapse ) {
            retVal = this.props.onCollapse( event, retVal, eventPhase );
        }

        if ( retVal ) {
            this.setState( {
                currentSelectedPropertySets    : ( [] as PropertySet[] ).concat( this.props.selectedPropertySets ?? [] ),
                currentSelectedEnumPropertySets: ( [] as PropertySet[] ).concat( this.props.selectedEnumPropertySets ?? [] ),
                currentSelectedReferenceSets   : {
                    dest: ( [] as ReferenceSet[] ).concat( this.props.selectedReferenceSets?.dest ?? [] ),
                    src : ( [] as ReferenceSet[] ).concat( this.props.selectedReferenceSets?.src ?? [] )
                }
            } );
        }

        return retVal;
    }

    onToggleAllButtonClick( type : 'properties' | 'enumProperties' | 'references' ) {
        let allSelectableSelected = true;

        if ( type === 'properties' ) {
            const allPropertySets = this.props.allPropertySets ?? [];

            let currentSelectedPropertySets = ( [] as PropertySet[] ).concat( this.state.currentSelectedPropertySets );

            for ( const propertySet of allPropertySets ) {
                if ( !currentSelectedPropertySets.find( pSet => pSet.id === propertySet.id ) ) {
                    allSelectableSelected = false;
                    currentSelectedPropertySets.push( propertySet );
                }
            }

            if ( allSelectableSelected ) {
                currentSelectedPropertySets = [];
            }

            this.setState( { currentSelectedPropertySets } );
        }

        else if ( type === 'enumProperties' ) {
            const allEnumPropertySets = this.props.allEnumPropertySets ?? [];

            let currentSelectedEnumPropertySets = ( [] as PropertySet[] ).concat( this.state.currentSelectedEnumPropertySets );

            for ( const enumPropertySet of allEnumPropertySets ) {
                if ( !currentSelectedEnumPropertySets.find( pSet => pSet.id === enumPropertySet.id ) ) {
                    allSelectableSelected = false;
                    currentSelectedEnumPropertySets.push( enumPropertySet );
                }
            }

            if ( allSelectableSelected ) {
                currentSelectedEnumPropertySets = [];
            }

            this.setState( { currentSelectedEnumPropertySets } );
        }

        else if ( type === 'references' ) {
            const referenceDirections          = Object.keys( this.props.allReferenceSets ) as ( 'dest' | 'src' )[],
                  allReferenceSets             = this.props.allReferenceSets ?? { dest: [], src: [] },
                  currentSelectedReferenceSets = {
                      dest: ( [] as ReferenceSet[] ).concat( this.state.currentSelectedReferenceSets.dest ),
                      src : ( [] as ReferenceSet[] ).concat( this.state.currentSelectedReferenceSets.src )
                  };

            for ( const referenceDirection of referenceDirections ) {
                for ( const referenceSet of allReferenceSets[ referenceDirection ] ) {
                    if ( !currentSelectedReferenceSets[ referenceDirection ].find( rSet => rSet.id === referenceSet.id ) ) {
                        allSelectableSelected = false;
                        currentSelectedReferenceSets[ referenceDirection ].push( referenceSet );
                    }
                }
            }

            if ( allSelectableSelected ) {
                currentSelectedReferenceSets[ 'dest' ] = [];
                currentSelectedReferenceSets[ 'src' ] = [];
            }

            this.setState( { currentSelectedReferenceSets } );
        }
    }

    onPropertyOrReferenceSelectionChange( type : SelectionType, id : number, event : InputSwitchChangeEvent ) {
        const checked = event.value;

        if ( type === 'properties' ) {
            const currentSelectedPropertySets = ( [] as PropertySet[] ).concat( this.state.currentSelectedPropertySets );

            if ( checked ) {
                const allPropertySets = this.props.allPropertySets,
                      propertySet     = allPropertySets?.find( pSet => pSet.id === id );

                if ( propertySet ) {
                    currentSelectedPropertySets.push( propertySet );
                }
            }
            else {
                const index = currentSelectedPropertySets.findIndex( pSet => pSet.id === id );

                if ( index !== -1 ) {
                    currentSelectedPropertySets.splice( index, 1 );
                }
            }

            this.setState( { currentSelectedPropertySets } );
        }

        else if ( type === 'enumProperties' ) {
            const currentSelectedEnumPropertySets = ( [] as PropertySet[] ).concat( this.state.currentSelectedEnumPropertySets );

            if ( checked ) {
                const allEnumPropertySets = this.props.allEnumPropertySets,
                      enumPropertySet     = allEnumPropertySets?.find( pSet => pSet.id === id );

                if ( enumPropertySet ) {
                    currentSelectedEnumPropertySets.push( enumPropertySet );
                }
            }
            else {
                const index = currentSelectedEnumPropertySets.findIndex( pSet => pSet.id === id );

                if ( index !== -1 ) {
                    currentSelectedEnumPropertySets.splice( index, 1 );
                }
            }

            this.setState( { currentSelectedEnumPropertySets: currentSelectedEnumPropertySets } );
        }

        else if ( type === 'destReferences' || type === 'srcReferences' ) {
            const currentSelectedReferenceSets = {
                dest: ( [] as ReferenceSet[] ).concat( this.state.currentSelectedReferenceSets.dest ),
                src : ( [] as ReferenceSet[] ).concat( this.state.currentSelectedReferenceSets.src )
            };

            if ( checked ) {
                const allReferenceSets = type === 'destReferences'
                          ? this.props.allReferenceSets?.dest ?? []
                          : this.props.allReferenceSets?.src ?? [],
                      referenceSet     = allReferenceSets.find( rSet => rSet.id === id );

                if ( referenceSet ) {
                    currentSelectedReferenceSets[ type === 'destReferences' ? 'dest' : 'src' ].push( referenceSet );
                }
            }
            else {
                const index = currentSelectedReferenceSets[ type === 'destReferences' ? 'dest' : 'src' ].findIndex( rSet => rSet.id === id );

                if ( index !== -1 ) {
                    currentSelectedReferenceSets[ type === 'destReferences' ? 'dest' : 'src' ].splice( index, 1 );
                }
            }

            this.setState( { currentSelectedReferenceSets } );
        }
    }

    getSelectors() {
        const retVal : JSX.Element[]                                            = [],
              {
                  uid,
                  allPropertySets,
                  selectablePropertySets,
                  allEnumPropertySets,
                  selectableEnumPropertySets,
                  allReferenceSets,
                  selectableReferenceSets,
                  referenceItemType,
                  rawMode,
                  showAdditionalRawNames
              }                                                                 = this.props,
              {
                  currentSelectedPropertySets,
                  currentSelectedEnumPropertySets,
                  currentSelectedReferenceSets
              }                                                                 = this.state,
              properties : { label : string; inputSwitch : JSX.Element; }[]     = [],
              enumProperties : { label : string; inputSwitch : JSX.Element; }[] = [],
              references : { label : string; inputSwitch : JSX.Element; }[]     = [];

        if ( allPropertySets ) {
            for ( const propertySet of allPropertySets ) {
                const key         = `properties_${propertySet.name}`,
                      label       = getPropertySetDisplayName( propertySet.id, propertySet.schema, rawMode, showAdditionalRawNames ),
                      selectable  = selectablePropertySets?.includes( propertySet ) ?? false,
                      selected    = currentSelectedPropertySets?.includes( propertySet ) ?? false,
                      inputSwitch = <div key={key} className="yn-selectiongroup-column">
                          <InputSwitch
                              inputId={`${key}_switch`}
                              checked={selected}
                              disabled={false}
                              onChange={this.onPropertyOrReferenceSelectionChange.bind( this, 'properties', propertySet.id )}
                          />
                          <label htmlFor={`${key}_switch`} className={!selectable ? 'p-disabled' : ''}>
                              {label}
                          </label>
                      </div>;

                properties.push( { label, inputSwitch } );
            }
        }

        if ( allEnumPropertySets ) {
            for ( const enumPropertySet of allEnumPropertySets ) {
                const key         = `properties_${enumPropertySet.name}`,
                      label       = getPropertySetDisplayName( enumPropertySet.id, enumPropertySet.schema, rawMode, showAdditionalRawNames ),
                      selectable  = selectableEnumPropertySets?.includes( enumPropertySet ) ?? false,
                      selected    = currentSelectedEnumPropertySets?.includes( enumPropertySet ) ?? false,
                      inputSwitch = <div key={key} className="yn-selectiongroup-column">
                          <InputSwitch
                              inputId={`${key}_switch`}
                              checked={selected}
                              disabled={false}
                              onChange={this.onPropertyOrReferenceSelectionChange.bind( this, 'enumProperties', enumPropertySet.id )}
                          />
                          <label htmlFor={`${key}_switch`} className={!selectable ? 'p-disabled' : ''}>
                              {label}
                          </label>
                      </div>;

                enumProperties.push( { label, inputSwitch } );
            }
        }

        if ( allReferenceSets ) {
            const referenceDirections = Object.keys( allReferenceSets ) as ( 'dest' | 'src' )[],
                  referenceItemTypeId = metaModel[ schema ].getItemType( referenceItemType ?? '' )?.id;

            for ( const referenceDirection of referenceDirections ) {
                for ( const referenceSet of allReferenceSets[ referenceDirection ] ) {
                    let label : string;

                    if ( referenceItemType && referenceItemTypeId ) {
                        label = getReferenceSetDisplayNameFromItemType( referenceSet.id, referenceItemType, referenceSet.schema, rawMode, showAdditionalRawNames, ( referenceDirection === 'src' ? 'dest' : 'src' ) as 'src' | 'dest' );
                    }
                    else {
                        label = getReferenceSetDisplayName( referenceSet.id, referenceSet.schema, rawMode, showAdditionalRawNames );
                    }

                    const key         = `references_${referenceDirection}_${referenceSet.id}`,
                          selectable  = selectableReferenceSets?.[ referenceDirection ].includes( referenceSet ) ?? false,
                          selected    = currentSelectedReferenceSets?.[ referenceDirection ].includes( referenceSet ) ?? false,
                          inputSwitch = <div key={key} className="yn-selectiongroup-column">
                              <InputSwitch
                                  inputId={`${key}_switch`}
                                  checked={selected}
                                  disabled={false}
                                  onChange={this.onPropertyOrReferenceSelectionChange.bind( this, referenceDirection === 'dest' ? 'destReferences' : 'srcReferences', referenceSet.id )}
                              />
                              <label htmlFor={`${key}_switch`} className={!selectable ? 'p-disabled' : ''}>
                                  {label}
                              </label>
                          </div>;

                    references.push( { label, inputSwitch } );
                }
            }
        }

        if ( properties.length ) {
            properties.sort( ( property1, property2 ) => {
                if ( property1.label > property2.label ) {
                    return 1;
                }
                else if ( property1.label < property2.label ) {
                    return -1;
                }
                else {
                    return 0;
                }
            } );

            retVal.push( <div key={`${uid}_properties`} className="yn-selectiongroup">
                <div key={`${uid}_properties_header`}
                     className="yn-selectiongroup-header">
                    {__( 'YN.base.finder.selectPropertiesAndReferences.properties.title' )}
                </div>
                <Button
                    id={`${uid}_toggleAllProperties`}
                    onClick={this.onToggleAllButtonClick.bind( this, 'properties' )}

                    label={__( 'YN.base.columnConfig.toggleAllColumns' )}
                    className="p-button-link yn-link-button"
                />
                <hr className="mt-1 yn-ruler"/>
                <div className="yn-selectiongroup-columns">
                    {properties.map( property => property.inputSwitch )}
                </div>
            </div> );
        }

        if ( enumProperties.length ) {
            enumProperties.sort( ( property1, property2 ) => {
                if ( property1.label > property2.label ) {
                    return 1;
                }
                else if ( property1.label < property2.label ) {
                    return -1;
                }
                else {
                    return 0;
                }
            } );

            let className = 'yn-selectiongroup';
            if ( properties.length ) {
                className += ' yn-selectiongroup-spacer';
            }

            retVal.push( <div key={`${uid}_enumProperties`} className={className}>
                <div key={`${uid}_enumProperties_header`}
                     className="yn-selectiongroup-header">
                    {__( 'YN.base.finder.selectPropertiesAndReferences.enumProperties.title' )}
                </div>
                <Button
                    id={`${uid}_toggleAllEnumProperties`}
                    onClick={this.onToggleAllButtonClick.bind( this, 'enumProperties' )}

                    label={__( 'YN.base.columnConfig.toggleAllColumns' )}
                    className="p-button-link yn-link-button"
                />
                <hr className="mt-1 yn-ruler"/>
                <div className="yn-selectiongroup-columns">
                    {enumProperties.map( property => property.inputSwitch )}
                </div>
            </div> );
        }

        if ( references.length ) {
            references.sort( ( reference1, reference2 ) => {
                if ( reference1.label > reference2.label ) {
                    return 1;
                }
                else if ( reference1.label < reference2.label ) {
                    return -1;
                }
                else {
                    return 0;
                }
            } );

            let className = 'yn-selectiongroup';
            if ( properties.length || enumProperties.length ) {
                className += ' yn-selectiongroup-spacer';
            }

            retVal.push( <div key={`${uid}_references`} className={className}>
                <div key={`${uid}_references_header`}
                     className="yn-selectiongroup-header">
                    {__( 'YN.base.finder.selectPropertiesAndReferences.references.title' )}
                </div>
                <Button
                    id={`${uid}_toggleAllReferences`}
                    onClick={this.onToggleAllButtonClick.bind( this, 'references' )}

                    label={__( 'YN.base.columnConfig.toggleAllColumns' )}
                    className="p-button-link yn-link-button"
                />
                <hr className="mt-1 yn-ruler"/>
                <div className="yn-selectiongroup-columns">
                    {references.map( reference => reference.inputSwitch )}
                </div>
            </div> );
        }

        return retVal;
    }

    render() {
        const {
                  uid,
                  id,
                  buttonClassName,
                  buttonIconClassName,
                  buttonTooltip,
                  menuTitle,
                  menuDescription,
                  className,
                  tabIndex
              } = this.props;

        return <ContentButton
            id={id}

            ref={element => this.contentButton = element}

            horizontalContentAlign="right"
            verticalContentAlign="bottom"
            rounded={true}
            text={true}
            icon={`pi ${buttonIconClassName}`}
            className={`yn-toolbar-button ${buttonClassName} ${className}`}
            contentClassName="yn-contentbutton-overlay-content-page-menu"
            tooltip={buttonTooltip}
            tooltipOptions={config.frontend?.buttonTooltipOptions}
            tabIndex={tabIndex}

            onExpand={this.onContentButtonExpand}
            onCollapse={this.onContentButtonCollapse}

            content={
                <Panel
                    className="yn-flex"
                    style={{
                        width: '50rem'
                    }}
                    pt={{
                        content: {
                            style: {
                                border: 'none'
                            }
                        }
                    }}
                >
                    <label
                        key={`${uid}_header`}

                        className="yn-menu-title-label"
                    >
                        {menuTitle}
                    </label>

                    {menuDescription && <>{menuDescription}<br/><br/></>}

                    <div className="flex" style={{ flex: 1, overflow: 'hidden', maxHeight: '50vh' }}>
                        {this.getSelectors()}
                    </div>

                    <hr className="mt-3 mb-3 yn-ruler"/>

                    <div className="flex justify-content-end">
                        <Button
                            key={`${uid}_createButton`}

                            label={__( 'YN.base.common.export' )}
                            onClick={this.onApplyButtonClick}
                            className="p-button-outlined mr-3 yn-button-defaultwidth text-center"
                        />
                        <Button
                            key={`${uid}_cancelButton`}

                            label={__( 'YN.base.common.cancel' )}
                            onClick={this.onCancelButtonClick}
                            className="p-button-secondary p-button-outlined yn-button-defaultwidth text-center"
                        />
                    </div>
                </Panel>
            }
        />;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

export default connect( mapStateToProps, null )( PropertiesAndReferencesSelectionMenu );
